import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from '../utils/axiosInstance';
import { calculateTotalAmount, calculateItemSubtotal, calculateSellingPriceIncludingTax } from '../utils/function';
import { formatCurrency } from '../utils/format';
import TableView from '../components/TableView';
import { useForm, Controller } from 'react-hook-form';
import PurchaseSupplierInfo from '../components/PurchaseSupplierInfo';
import LabelValueItem from '../components/LabelValueItem';
import { Paper, Grid, Typography, Button, TextField, Alert, IconButton, Divider, Stack, Box, MenuItem, Tooltip } from '@mui/material';
import { Add, LibraryAdd, Delete, HelpOutline } from '@mui/icons-material'
import InputFieldAddress from '../components/InputFieldAddress';
import usePurchaseTable from '../hooks/usePurchaseTable';
const PurchaseNewPage = ({ hasLimit }) => {
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const isEditMode = true;
    const [fieldErrors, setFieldErrors] = useState({
        items: []
    });
    const [warehouses, setWarehouses] = useState([]);
    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const res = await axiosInstance.get('/warehouse', {
                    params: { activeOnly: true }
                });
                setWarehouses(res.data);
            } catch (err) {
                if (process.env.NODE_ENV === 'development') {
                    console.error(err);
                }
            }
        };
        fetchWarehouses();
    }, []);
    // 新規発注データの管理
    const [purchaseData, setPurchaseData] = useState({
        number: '',
        supplier_name: '',
        supplier_id: null,
        supplier_contact_id: null,
        supplier_contact_name: '',
        supplier_contact_phone: '',
        shipping_address_id: null,
        postal_code: '',
        state: '',
        address_line: '',
        delivery_date: null,
        recipient_name: '',
        payment_terms: '',
        payment_method_name: '',
        total_amount: 0,
        total_amount_including_tax: 0,
        items: [],
        notes: '',
        warehouse_id: ""
    });
    const { getValues, register, handleSubmit, control, formState: { errors }, setValue } = useForm({
        defaultValues: purchaseData,
    });
    // 発注アイテムの削除
    const handleItemDelete = (index) => {
        const updatedItems = purchaseData.items.filter((_, i) => i !== index);
        setPurchaseData({ ...purchaseData, items: updatedItems });
    };
    const {
        columns,
        updateItem,
        handleItemChange,
    } = usePurchaseTable({
        control,
        errors,
        isEditMode,
        hasLimit,
        axiosInstance,
        selectedSupplier,
        purchaseData,
        setPurchaseData,
        calculateSellingPriceIncludingTax,
        calculateItemSubtotal,
        calculateTotalAmount,
        formatCurrency,
        handleItemDelete,
    });
    // アイテムの追加
    const addItem = () => {
        const newItem = {
            product_id: null,
            variant_id: null,
            sku: '',
            quantity: 1,
            purchase_price: 0,
            purchase_price_including_tax: 0,
            tax_rate: 0,
            subtotal: 0,
            subtotal_including_tax: 0,
        };

        const newItems = [...purchaseData.items, newItem];

        setPurchaseData({
            ...purchaseData,
            items: newItems,
        });
        setValue(`items.${newItems.length - 1}`, newItem);
    };

    // 新規発注の送信
    const onSubmit = async (data) => {
        const submitData = {
            ...purchaseData,
            ...data,
            date: null,
            items: purchaseData.items,
            total_amount: purchaseData.total_amount,
            total_amount_including_tax: purchaseData.total_amount_including_tax
        };
        try {
            // 発注データを送信
            const res = await axiosInstance.post('/purchase/new', { purchaseData: submitData });
            navigate(`/purchases/${res.data.id}`, {
                state: { snackbar: { message: res?.data?.message || '発注が作成されました。', severity: 'success' } }
            });
        } catch (err) {
            if (process.env.NODE_ENV === 'development') {
                console.error(err);
            }
            showSnackbar(err?.response?.data?.message || '発注作成に失敗しました。', 'error');
        }
    };
    return (
        <>
            <Typography variant="h1" component="h1">新規発注作成</Typography>
            <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                <Paper variant='section'>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Link to="/products/new">商品登録</Link> と <Link to="/suppliers" >仕入れ先管理</Link> での商品紐づけを行ってから作成を開始しましょう。
                    </Alert>
                    <Controller
                        name="warehouse_id"
                        control={control}
                        rules={{ required: "倉庫を選択してください" }}
                        render={({ field }) => (
                            <TextField
                                select
                                label="入庫倉庫"
                                {...field}
                                value={field.value ?? ''}
                                disabled={!isEditMode}
                                error={!!errors.warehouse_id}
                                helperText={errors.warehouse_id?.message}
                                onChange={(e) => {
                                    const value = Number(e.target.value);
                                    field.onChange(value);
                                    const selected = warehouses.find(
                                        w => Number(w.warehouse_id) === value
                                    );
                                    if (selected) {
                                        const currentPostal = getValues('postal_code');
                                        const currentState = getValues('state');
                                        const currentAddress = getValues('address_line');

                                        const isAllEmpty =
                                            !currentPostal &&
                                            !currentState &&
                                            !currentAddress;

                                        if (isAllEmpty) {
                                            setValue('postal_code', selected.postal_code || '', { shouldDirty: true });
                                            setValue('state', selected.state || '', { shouldDirty: true });
                                            setValue('address_line', selected.address_line || '', { shouldDirty: true });
                                        }
                                    }
                                }}
                            >
                                {warehouses.map((wh) => (
                                    <MenuItem key={wh.warehouse_id} value={wh.warehouse_id}>
                                        {wh.warehouse_name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    />
                </Paper>
                <Paper variant='section'>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}><LabelValueItem label='発注日時' value='（作成日時）' /></Grid>
                        <Grid item xs={12} sm={6}><LabelValueItem label='発注番号' value='（自動採番）' /></Grid>
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                    <Typography component='h2' variant="h4" mb={2}>発注先情報</Typography>
                    <PurchaseSupplierInfo
                        hasLimit={hasLimit}
                        isEditMode={isEditMode}
                        register={register}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                        fieldErrors={fieldErrors}
                        setFieldErrors={setFieldErrors}
                        setSelectedSupplier={setSelectedSupplier}
                    />
                    <Divider sx={{ my: 2 }} />
                    <Stack direction='row' alignItems='center' mb={2}>
                        <Typography component='h2' variant="h4" sx={{ mb: 0 }}>納入先情報</Typography>
                        <Tooltip
                            title={
                                <Typography variant="body2">
                                    倉庫管理で倉庫の住所を保存すると、自動入力できます。
                                </Typography>
                            }
                            arrow
                            placement="right"
                        >
                            <IconButton size="small" aria-label="help">
                                <HelpOutline fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <InputFieldAddress
                        register={register}
                        errors={errors}
                        isEditMode={isEditMode}
                        setValue={setValue}
                        control={control}
                        label='納入先住所'
                    />
                    <Divider sx={{ my: 2 }} />
                    <TextField
                        label="備考"
                        multiline
                        minRows={3}
                        disabled={!isEditMode}
                        error={!!errors.notes}
                        helperText={errors.notes?.message}
                        {...register('notes', {
                            maxLength: {
                                value: 2000,
                                message: '2000文字以内で入力してください',
                            },
                        })}
                        inputProps={{ maxLength: 2000 }}
                        sx={{ mt: 2 }}
                    />
                    <Divider sx={{ my: 2 }} />
                    <Stack direction='row' alignItems='center'>
                        <Typography component='h2' variant="h4" sx={{ mb: 0 }}>商品情報</Typography>
                        <Tooltip
                            title={
                                <Typography variant="body2">
                                    先に仕入れ先管理で取扱商品を登録しましょう。品番は自社の品番から検索できます。
                                </Typography>
                            }
                            arrow
                            placement="right"
                        >
                            <IconButton size="small" aria-label="help">
                                <HelpOutline fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <Alert severity="info">
                        先に上部の発注先情報で仕入先を選択してください。<br />
                        <Link to="/products/new">商品登録</Link>した品番から検索し、商品を選択してください。<Link to="/suppliers">仕入れ先管理</Link>で紐づけた取扱商品のみが検索対象です。
                    </Alert>
                    <TableView dataset={purchaseData.items} columns={columns} keyColumn={(row, index) => index} />
                    {fieldErrors.items && <span className="error-message font-s">{fieldErrors.items}<br /></span>}
                    <Button
                        type="button"
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={addItem}
                        size='small'
                        sx={{ mt: 1 }}
                    >アイテムを追加</Button>
                    {/* 合計金額 */}
                    <Box sx={{ maxWidth: 400, ml: 'auto', mt: 2 }}>
                        <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography fontWeight="bold">合計金額（税抜）</Typography>
                                <Typography fontWeight="bold">{formatCurrency(purchaseData.total_amount ?? 0)}</Typography>
                            </Stack>
                            <Divider />
                            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                                <Typography fontWeight="bold">合計金額（税込）</Typography>
                                <Typography fontSize={22} fontWeight="bold">{formatCurrency(purchaseData.total_amount_including_tax ?? 0)}</Typography>
                            </Stack>
                            <Divider />
                        </Stack>
                    </Box>
                    <div className='button-row'>
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<LibraryAdd />}
                        >発注を作成</Button>
                    </div>
                </Paper>
            </form>
        </>
    );
};
export default PurchaseNewPage;